import classnames from "classnames";
import React, { useCallback, useState } from "react";
import { Redirect } from "react-router";
import { useParams } from "react-router-dom";
import Page from "../../../components/Page";
import { useWebGalleryStore } from "../../../hooks";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { Button } from "../../../lib/Button";
import "./Authorize.css";
import LockSVG from "./LockSVG";

type AuthorizeProps = {
  galleryId: string;
  assetId?: string;
  handleAuthorization: (
    galleryId: string,
    password: string
  ) => Promise<boolean>;
};

interface AuthorizationFormElement extends HTMLFormElement {
  galleryPasswordInput: HTMLInputElement;
}

export const AuthorizeTemplate = (props: AuthorizeProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [galleryPassword, setGalleryPassword] = useState<string | undefined>();

  const { t } = useCustomTranslation("Authorization");

  const submitHandler = useCallback(
    async (event: React.FormEvent<AuthorizationFormElement>) => {
      const { galleryId, handleAuthorization } = props;
      const password = event.currentTarget.galleryPasswordInput?.value;

      event.preventDefault();

      if (!password) {
        setError(new Error(t("PasswordRequired") as string));
        return;
      }

      setIsSubmitting(true);

      try {
        const isAuthorized = await handleAuthorization(galleryId, password);

        setIsSubmitting(false);

        if (!isAuthorized) {
          setError(new Error(t("PasswordIncorrect") as string));
        } else {
          setError(null);
        }
        setIsAuthorized(isAuthorized);
      } catch (error) {
        console.error(error);
        setError(new Error(t("RequestProblem") as string));
      }
    },
    [isAuthorized]
  );

  if (isAuthorized) {
    if (props.assetId) {
      return (
        <Redirect to={`/${props.galleryId}-ExternalUser/${props.assetId}`} />
      );
    } else {
      return <Redirect to={`/${props.galleryId}-ExternalUser`} />;
    }
  }

  const inputClassNames = classnames({
    "has-error": !!error,
  });

  return (
    <Page>
      <div className="container">
        <div className="content">
          <LockSVG />
          <h1>{t("PasswordProtected")}</h1>
          <p>{t("EnterPassword")}</p>
          <form
            method="POST"
            onSubmit={submitHandler}
            onInput={() => setError(null)}
          >
            <input
              disabled={isSubmitting}
              type="password"
              placeholder={t("Password") as string}
              name="galleryPasswordInput"
              autoComplete="off"
              aria-label={t("Password") as string}
              className={inputClassNames}
              onChange={(event) => setGalleryPassword(event.target.value)}
            />
            <Button type="submit" disabled={isSubmitting || !galleryPassword}>
              {isSubmitting ? t("Loading") : t("Submit")}
            </Button>
          </form>
          {renderError(error)}
        </div>
      </div>
    </Page>
  );
};

function renderError(error: Error | null) {
  if (!error) {
    return null;
  }

  return <p className="has-error">{error.message}</p>;
}

AuthorizeTemplate.defaultProps = {
  handleAuthorization: () => false,
};

const AuthorizeController = (props: any) => {
  const { galleryId } = useParams<{ galleryId: string }>();
  const { assetId } = useParams<{ assetId: string }>();
  const store = useWebGalleryStore();
  const handleAuthorization = async (galleryId: string, password: string) => {
    return await store.api.authorizeGallery(galleryId, password);
  };
  return (
    <AuthorizeTemplate
      assetId={assetId}
      galleryId={galleryId}
      handleAuthorization={handleAuthorization}
    />
  );
};

export default AuthorizeController;
